import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import axios from 'axios';

/**
 * Where "View All Notifications" should go for the dashboard this bell is rendered in.
 *
 * The bell is used on the owner sidebar, the professional sidebar and the freelancer sidebar, and the link was
 * hardcoded to `/dashboard/professional/notifications` — so an owner or a freelancer was sent to a page that is
 * not theirs (and, for R1, one the route guard refuses). Each layout passes `viewAllPath`; this is the fallback
 * for the role written into localStorage.
 */
function defaultViewAllPath() {
  const role = (
    localStorage.getItem('ogestyle_role_override') ||
    localStorage.getItem('ogestyle_role') ||
    ''
  ).toUpperCase();
  if (role === 'R3') return '/dashboard/professional/notifications';
  if (role === 'R4' || role === 'R7') return '/dashboard/freelancer/notifications';
  return '/dashboard/notifications';
}

const DROPDOWN_WIDTH = 360;
const VIEWPORT_MARGIN = 12;

function NotificationBell({ viewAllPath }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  };

  // Get API base URL
  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL;
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/notifications`, { 
        headers,
        params: { limit: 10 } // Get last 10 notifications
      });

      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, []);

  /**
   * The panel is drawn in a portal on `document.body` with `position: fixed`.
   *
   * It used to be `position: absolute` inside the sidebar, and `.dashboard-sidebar` sets
   * `overflow-x: hidden; overflow-y: hidden` — so the dropdown was clipped to the sidebar's 272px and appeared
   * cut off (and, on a narrow screen, outside the window). A portal cannot be clipped by any ancestor, and the
   * coordinates below clamp the panel inside the viewport for every role and screen size.
   */
  const computePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return null;
    const rect = button.getBoundingClientRect();
    const width = Math.min(DROPDOWN_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);

    // Prefer aligning the panel's right edge with the bell; fall back to its left edge when that would push
    // the panel off the left of the screen (a collapsed 52px sidebar puts the bell near the window edge).
    let left = rect.right - width;
    if (left < VIEWPORT_MARGIN) left = rect.left;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN));

    // Open downwards unless there is not enough room, in which case open upwards.
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - VIEWPORT_MARGIN;
    const openUpwards = spaceBelow < 260 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(200, Math.min(500, openUpwards ? spaceAbove : spaceBelow));

    return {
      left,
      width,
      maxHeight,
      top: openUpwards ? undefined : rect.bottom + 8,
      bottom: openUpwards ? window.innerHeight - rect.top + 8 : undefined,
    };
  }, []);

  const openDropdown = () => {
    // Compute before flipping: the panel is portalled, so it needs real coordinates on its very first render.
    if (!showDropdown) setPosition(computePosition());
    setShowDropdown((previous) => !previous);
  };

  // Close dropdown when clicking outside (the panel lives in a portal, so it is not inside dropdownRef).
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inButton = dropdownRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inButton && !inPanel) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // A fixed panel would drift away from its bell as soon as the page scrolls or the window resizes.
  useEffect(() => {
    if (!showDropdown) return undefined;
    const reposition = () => setPosition(computePosition());
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [showDropdown, computePosition]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/notifications/${notificationId}/read`,
        {},
        { headers }
      );

      // Update local state
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    setLoading(true);
    
    // Mark as read
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    // Navigate to action URL if present
    if (notification.actionUrl) {
      // Validate URL starts with /dashboard/ for security
      if (notification.actionUrl.startsWith('/dashboard/')) {
        navigate(notification.actionUrl);
      }
    }

    setShowDropdown(false);
    setLoading(false);
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.post(`${baseURL}/api/notifications/mark-all-read`, {}, { headers });

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
    }
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'package_created':
        return '📦';
      case 'package_updated':
        return '🔄';
      case 'booking':
        return '📅';
      case 'system':
        return '⚙️';
      default:
        return '🔔';
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bell Icon */}
      <button
        ref={buttonRef}
        onClick={openDropdown}
        aria-haspopup="true"
        aria-expanded={showDropdown}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          color: '#2d6da3',
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <FaBell />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: '#ef4444',
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            borderRadius: '10px',
            padding: '2px 6px',
            minWidth: '18px',
            textAlign: 'center'
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — portalled so the sidebar's overflow cannot clip it */}
      {showDropdown && position && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'fixed',
            top: position.top,
            bottom: position.bottom,
            left: position.left,
            width: position.width,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            zIndex: 2147483000,
            maxHeight: position.maxHeight,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2d6da3',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div style={{
            overflowY: 'auto',
            maxHeight: '400px'
          }}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#9ca3af'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
                <div style={{ fontSize: '14px' }}>No notifications yet</div>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: notification.actionUrl ? 'pointer' : 'default',
                    background: notification.isRead ? 'white' : '#f0f9ff',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (notification.actionUrl) e.currentTarget.style.background = '#e0f2fe';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.isRead ? 'white' : '#f0f9ff';
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {/* Icon */}
                    <div style={{ fontSize: '24px', flexShrink: 0 }}>
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: notification.isRead ? '400' : '600',
                        color: '#1e293b',
                        marginBottom: '4px',
                        lineHeight: '1.4'
                      }}>
                        {notification.title}
                      </div>
                      {notification.message && (
                        <div style={{
                          fontSize: '13px',
                          color: '#64748b',
                          lineHeight: '1.4',
                          marginBottom: '6px'
                        }}>
                          {notification.message}
                        </div>
                      )}
                      <div style={{
                        fontSize: '12px',
                        color: '#9ca3af'
                      }}>
                        {formatTimeAgo(notification.createdAt)}
                      </div>
                    </div>

                    {/* Unread indicator */}
                    {!notification.isRead && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        background: '#3b82f6',
                        borderRadius: '50%',
                        flexShrink: 0,
                        marginTop: '6px'
                      }} />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer - View All Link */}
          {notifications.length > 0 && (
            <div style={{
              padding: '12px',
              borderTop: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <button
                onClick={() => {
                  navigate(viewAllPath || defaultViewAllPath());
                  setShowDropdown(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2d6da3',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default NotificationBell;
